import {
  formaterJour,
  jourAParis,
  TYPES_ACCES_BANQUE,
  type TypeAccesBanque,
  typeAccesRetenu,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { useState } from 'react'
import { api, ErreurApi, lire, requetes } from '../api'
import { formaterInstant } from '../composants/Historique'
import { Logo, logoDeBanque } from '../composants/Logo'
import { Section, Vide } from '../composants/Section'
import { confirmer } from '../composants/confirmation'

// Réglages, « Banques » (administrateurs) : accès DSP2 en lecture seule par l'agrégateur,
// comptes remontés et leur rattachement, relève, reconnexion. La validation forte se fait
// dans l'appli de la banque ; aucun identifiant bancaire ne passe par ici.

type Acces = InferResponseType<typeof api.banques.$get, 200>[number]
type CompteRemonte = Acces['comptes'][number]

const RESULTATS: Record<string, string> = {
  connectee:
    'Accès enregistré. Rattachez maintenant chaque compte remonté à un compte d’une entité : il sera relevé à 5 h 50 et 17 h 50, ou tout de suite avec « Relever maintenant ».',
  annulee: 'Connexion annulée à la banque.',
  echec: 'La connexion a échoué : recommencez.',
  refusee: 'Cet accès n’a pas pu être enregistré : rien n’a été gardé.',
  inconnue: 'Banque inconnue de l’agrégateur : choisissez-la dans la liste.',
  indisponible: 'Les accès bancaires ne sont pas configurés, ou l’agrégateur ne répond pas.',
}

const TYPES: Record<TypeAccesBanque, string> = {
  particulier: 'Particulier',
  professionnel: 'Professionnel',
}

function connecter(banque: string, pays: string, type: TypeAccesBanque) {
  const q = new URLSearchParams({ banque, pays, type })
  window.location.assign(`/api/banques/connecter?${q}`)
}

function etatAcces(a: Acces): { libelle: string; alerte: boolean } {
  if (a.etat === 'reconnexion') return { libelle: 'À reconnecter', alerte: true }
  if (a.suivi === 'a_renouveler') return { libelle: 'À renouveler', alerte: true }
  return { libelle: 'Relevé', alerte: false }
}

function Rattacher({ compte, banque }: { compte: CompteRemonte; banque: string }) {
  const client = useQueryClient()
  const entites = (useQuery(requetes.entites).data ?? []).filter((e) => !e.archivee)
  const [entiteId, setEntiteId] = useState('')
  const comptes = useQuery({ ...requetes.comptes(entiteId), enabled: entiteId !== '' }).data
  const [cible, setCible] = useState('nouveau')
  const [erreur, setErreur] = useState<string | null>(null)
  const libre = (comptes ?? []).filter((c) => c.source === 'csv')
  return (
    <form
      className="section-ajout"
      onSubmit={async (e) => {
        e.preventDefault()
        setErreur(null)
        try {
          await lire(
            api.banques.comptes[':id'].lier.$post({
              param: { id: compte.id },
              json:
                cible === 'nouveau'
                  ? { entiteId, libelle: compte.libelle ?? `Compte ${banque}` }
                  : { compteId: cible },
            }),
          )
          await client.invalidateQueries({ queryKey: ['banques'] })
          await client.invalidateQueries({ queryKey: ['comptes'] })
        } catch (x) {
          setErreur(x instanceof ErreurApi ? x.message : 'Rattachement impossible.')
        }
      }}
    >
      <div className="formulaire">
        <div className="champ">
          <label htmlFor={`entite-${compte.id}`}>Entité</label>
          <select
            id={`entite-${compte.id}`}
            required
            value={entiteId}
            onChange={(e) => {
              setEntiteId(e.target.value)
              setCible('nouveau')
            }}
          >
            <option value="">Choisir…</option>
            {entites.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        </div>
        {entiteId && (
          <div className="champ">
            <label htmlFor={`compte-${compte.id}`}>Compte de l’entité</label>
            <select
              id={`compte-${compte.id}`}
              value={cible}
              onChange={(e) => setCible(e.target.value)}
            >
              <option value="nouveau">Nouveau compte « {compte.libelle ?? banque} »</option>
              {libre.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.libelle} ({c.banque}, import CSV
                  {c.releveCouvertJusquAu
                    ? ` jusqu’au ${formaterJour(c.releveCouvertJusquAu)}`
                    : ''}
                  )
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
      <div className="boutons">
        <button type="submit" className="bouton" disabled={!entiteId}>
          Rattacher
        </button>
      </div>
    </form>
  )
}

function LigneCompte({ compte, banque }: { compte: CompteRemonte; banque: string }) {
  const client = useQueryClient()
  const [ouvert, setOuvert] = useState(false)
  return (
    <div className="ligne">
      <div className="ligne-contenu">
        <strong>{compte.libelle ?? 'Compte'}</strong>
        {compte.ibanMasque && <span className="discret"> {compte.ibanMasque}</span>}
        <div className="discret">
          {compte.compteId
            ? `Relevé dans « ${compte.compte} » (${compte.entite})${compte.releveCouvertJusquAu ? `, couvert jusqu’au ${formaterJour(compte.releveCouvertJusquAu)}` : ', pas encore relevé'}`
            : compte.disponible
              ? 'Pas rattaché : pas relevé'
              : 'Absent de la dernière connexion : pas relevé'}
        </div>
        {!compte.compteId && compte.disponible && ouvert && (
          <Rattacher compte={compte} banque={banque} />
        )}
      </div>
      <div className="boutons">
        {compte.compteId ? (
          <button
            type="button"
            className="bouton-discret"
            onClick={async () => {
              if (
                !(await confirmer(
                  `Ne plus relever « ${compte.compte} » par la banque ? Il reviendra à l’import CSV ; les opérations déjà relevées restent.`,
                ))
              )
                return
              await lire(api.banques.comptes[':id'].delier.$post({ param: { id: compte.id } }))
              await client.invalidateQueries({ queryKey: ['banques'] })
              await client.invalidateQueries({ queryKey: ['comptes'] })
            }}
          >
            Détacher
          </button>
        ) : (
          compte.disponible &&
          !ouvert && (
            <button type="button" className="bouton" onClick={() => setOuvert(true)}>
              Rattacher
            </button>
          )
        )}
      </div>
    </div>
  )
}

function LigneAcces({ acces, onMessage }: { acces: Acces; onMessage: (m: string) => void }) {
  const client = useQueryClient()
  const [occupe, setOccupe] = useState(false)
  const etat = etatAcces(acces)
  return (
    <div className="carte-banque">
      <div className="ligne">
        <div className="ligne-contenu">
          <div className="titre-logo">
            <Logo cle={logoDeBanque(acces.banque)} nom={acces.banque} />
            <strong>{acces.banque}</strong>
          </div>
          <span className={`badge ${etat.alerte ? 'retard-en_retard' : ''}`}>{etat.libelle}</span>
          <div className="discret">
            {TYPES[acces.type]} · donné par {acces.personne}, valable jusqu’au{' '}
            {formaterJour(jourAParis(new Date(acces.expireLe)))}
            {' · '}
            {acces.releveLe
              ? `dernière relève le ${formaterInstant(acces.releveLe)}`
              : 'pas encore relevé'}
          </div>
          {acces.derniereErreur && <div className="discret">{acces.derniereErreur}</div>}
        </div>
        <div className="boutons">
          {acces.etat === 'actif' && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={async () => {
                setOccupe(true)
                try {
                  const r = await lire(
                    api.banques[':id'].relever.$post({ param: { id: acces.id } }),
                  )
                  onMessage(
                    r.reconnexion
                      ? 'La banque demande une reconnexion.'
                      : r.limite
                        ? 'La banque limite les lectures : nouvel essai à la prochaine relève.'
                        : `${r.comptes} compte(s) relevé(s), ${r.lignesNouvelles} opération(s) nouvelle(s).`,
                  )
                } catch (x) {
                  onMessage(x instanceof ErreurApi ? x.message : 'Relève impossible.')
                } finally {
                  setOccupe(false)
                  await client.invalidateQueries({ queryKey: ['banques'] })
                }
              }}
            >
              Relever maintenant
            </button>
          )}
          {(acces.etat === 'reconnexion' || acces.suivi !== 'valide') && (
            <button
              type="button"
              className="bouton"
              onClick={() => connecter(acces.banque, acces.pays, acces.type)}
            >
              Reconnecter
            </button>
          )}
          <button
            type="button"
            className="bouton-discret"
            onClick={async () => {
              if (
                !(await confirmer(
                  `Retirer l’accès à ${acces.banque} ? Ses comptes reviennent à l’import CSV ; les opérations déjà relevées restent.`,
                ))
              )
                return
              const r = await lire(
                api.banques[':id'].deconnecter.$post({ param: { id: acces.id } }),
              )
              onMessage(
                r.fermeeChezAgregateur
                  ? `Accès à ${acces.banque} retiré.`
                  : `Accès à ${acces.banque} retiré ici, mais l’agrégateur n’a pas répondu : retirez-le aussi depuis l’appli de la banque.`,
              )
              await client.invalidateQueries({ queryKey: ['banques'] })
              await client.invalidateQueries({ queryKey: ['comptes'] })
            }}
          >
            Déconnecter
          </button>
        </div>
      </div>
      {acces.comptes.length === 0 && <Vide>Aucun compte remonté par la banque.</Vide>}
      {acces.comptes.map((c) => (
        <LigneCompte key={c.id} compte={c} banque={acces.banque} />
      ))}
    </div>
  )
}

function NouvelAcces() {
  const [ouvert, setOuvert] = useState(false)
  const disponibles = useQuery({ ...requetes.banquesDisponibles, enabled: ouvert })
  const [recherche, setRecherche] = useState('crédit agricole')
  const [banque, setBanque] = useState('')
  const [type, setType] = useState<TypeAccesBanque>('particulier')
  if (!ouvert)
    return (
      <div className="boutons">
        <button type="button" className="bouton" onClick={() => setOuvert(true)}>
          Connecter une banque
        </button>
      </div>
    )
  const simplifier = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
  const liste = (disponibles.data ?? []).filter((b) =>
    simplifier(`${b.nom} ${b.groupe ?? ''}`).includes(simplifier(recherche.trim())),
  )
  const choisie = liste.find((b) => b.nom === banque)
  // L'espace affiché est celui envoyé : une banque qui n'a que l'espace professionnel
  // (Swan) ne garde pas « particulier », la valeur de départ.
  const types = choisie?.types ?? []
  const espace = typeAccesRetenu(types, type)
  return (
    <form
      className="section-ajout"
      onSubmit={(e) => {
        e.preventDefault()
        if (choisie) connecter(choisie.nom, choisie.pays, espace)
      }}
    >
      {disponibles.error && (
        <p className="alerte">
          {disponibles.error instanceof ErreurApi
            ? disponibles.error.message
            : 'Liste des banques indisponible.'}
        </p>
      )}
      <div className="formulaire">
        <div className="champ">
          <label htmlFor="recherche-banque">Rechercher</label>
          <input
            id="recherche-banque"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
          />
        </div>
        <div className="champ">
          <label htmlFor="choix-banque">Banque (votre caisse régionale)</label>
          <select
            id="choix-banque"
            required
            value={banque}
            onChange={(e) => setBanque(e.target.value)}
          >
            <option value="">{disponibles.isLoading ? 'Chargement…' : 'Choisir…'}</option>
            {liste.map((b) => (
              <option key={b.nom} value={b.nom}>
                {b.nom}
                {b.beta ? ' (bêta)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="type-banque">Espace</label>
          <select
            id="type-banque"
            value={espace}
            onChange={(e) => setType(e.target.value as TypeAccesBanque)}
          >
            {(types.length ? types : TYPES_ACCES_BANQUE).map((t) => (
              <option key={t} value={t}>
                {TYPES[t]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="boutons">
        <button type="submit" className="bouton" disabled={!choisie}>
          Se connecter à la banque
        </button>
        <button type="button" className="bouton-discret" onClick={() => setOuvert(false)}>
          Annuler
        </button>
      </div>
      <p className="discret">
        La banque vous demandera de valider dans son appli (« Ma Banque » au Crédit Agricole). Un
        accès par espace : les comptes de la SC PMVB sont en général dans l’espace professionnel.
      </p>
    </form>
  )
}

export function Banques({ resultat }: { resultat: string | undefined }) {
  const acces = useQuery(requetes.banques)
  const [message, setMessage] = useState<string | null>(
    resultat ? (RESULTATS[resultat] ?? null) : null,
  )
  return (
    <Section titre="Banques">
      <p className="discret">
        L’appli lit vos comptes, en lecture seule, par l’agrégateur Enable Banking : elle ne peut ni
        payer ni virer, et ne voit jamais vos identifiants. L’accès dure au plus 180 jours ; une
        tâche vous prévient sept jours avant la fin. Les opérations sont relevées deux fois par
        jour, puis rapprochées des paiements attendus.
      </p>
      {message && <p className="alerte">{message}</p>}
      {acces.data?.length === 0 && <Vide>Aucune banque connectée.</Vide>}
      {acces.data?.map((a) => (
        <LigneAcces key={a.id} acces={a} onMessage={setMessage} />
      ))}
      <NouvelAcces />
    </Section>
  )
}
